import { inject, Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { environment } from '../../environments/environment'
import { Observable } from 'rxjs'

/* ── Enums (contract ch.9 of the marketplace PRD) ─────────────────────────── */

export type EquipmentCondition =
	| 'SEALED_NEW'
	| 'NEW_UNSEALED'
	| 'LIKE_NEW'
	| 'USED_HEALTHY'
	| 'USED_MINOR_FLAW'
	| 'NEEDS_REPAIR'
	| 'STOCK'
	| 'SHOWCASE'

export type EquipmentSaleKind = 'SINGLE' | 'WHOLESALE' | 'BOTH'

export type EquipmentSpecialUse =
	| 'GENERAL'
	| 'SPECIALIZED'
	| 'TRAINED_OPERATOR'
	| 'PHYSICIAN_SUPERVISION'
	| 'LICENSE_REQUIRED'
	| 'UNSURE'

export type EquipmentAuthenticity = 'ORIGINAL_DECLARED' | 'DOMESTIC' | 'IMPORTED' | 'UNKNOWN'

export type EquipmentSwapMode = 'NONE' | 'ONLY_SWAP' | 'SALE_OR_SWAP'

export type EquipmentDeliveryMethod =
	| 'IN_PERSON'
	| 'COURIER'
	| 'POST'
	| 'TIPAX'
	| 'FREIGHT'
	| 'SELLER_SHIPPING'
	| 'OTHER'

export type EquipmentShippingCostBearer = 'FREE' | 'BUYER' | 'SELLER' | 'NEGOTIATED' | 'BY_DESTINATION'

export type EquipmentReturnCostBearer = 'BUYER' | 'SELLER' | 'CASE_BY_CASE'

export type EquipmentCtaType = 'MESSAGE' | 'PHONE' | 'WHATSAPP'

export type EquipmentSaleMode = 'NORMAL'

export interface EquipmentCtaRow {
	type?: EquipmentCtaType | null
	value?: string | null
	label?: string | null
	primary?: boolean | null
	sortOrder?: number | null
}

export interface EquipmentPriceTier {
	minQty?: number | null
	unitPrice?: number | null
}

export interface SaveEquipmentAdRequest {
	id?: string | null
	title?: string | null
	shortDescription?: string | null
	description?: string | null
	categoryId?: string | null
	subcategoryId?: string | null
	brandId?: string | null
	brandTitle?: string | null
	modelId?: string | null
	modelTitle?: string | null
	countryOfOrigin?: string | null
	manufactureYear?: number | null
	inventoryCount?: number | null
	saleKind?: EquipmentSaleKind | null
	condition?: EquipmentCondition | null
	conditionFlawDesc?: string | null
	usageDurationMonths?: number | null
	usageMeter?: string | null
	repaired?: boolean | null
	repairDesc?: string | null
	accessories?: string | null
	hasBox?: boolean | null
	hasManual?: boolean | null
	hasInvoice?: boolean | null
	hasWarranty?: boolean | null
	warrantyRemainingMonths?: number | null
	specialUse?: EquipmentSpecialUse | null
	specialUseDesc?: string | null
	authenticity?: EquipmentAuthenticity | null
	serialNumber?: string | null
	serialPrivate?: boolean | null
	warrantyCard?: boolean | null
	supplierName?: string | null
	price?: number | null
	discountPrice?: number | null
	discountFrom?: string | null
	discountTo?: string | null
	negotiable?: boolean | null
	contactForPrice?: boolean | null
	wholesaleMinQty?: number | null
	priceTiers?: EquipmentPriceTier[] | null
	swapMode?: EquipmentSwapMode | null
	/** تیک «مایل به معاوضه هستم» — ستون اختصاصی بک‌اند. */
	isExchangeable?: boolean | null
	swapFor?: string | null
	swapNotes?: string | null
	cityId?: string | null
	detailAddress?: string | null
	deliveryArea?: string | null
	deliveryMethods?: EquipmentDeliveryMethod[] | null
	shippingCostBearer?: EquipmentShippingCostBearer | null
	shippingCostAmount?: number | null
	securePaymentEnabled?: boolean | null
	acceptsReturn?: boolean | null
	returnWindowDays?: number | null
	returnConditions?: string | null
	returnCostBearer?: EquipmentReturnCostBearer | null
	mediaFileIds?: string[] | null
	contactNumber?: string | null
	ctas?: EquipmentCtaRow[] | null
	dynamicValues?: Record<string, string | number | boolean | string[] | null> | null
	saleMode?: EquipmentSaleMode | null
	/** TKT-44694: «مناسب برای» — سالن زیبایی / کلینیک زیبایی / هر دو (ستون اختصاصی بک‌اند). */
	suitableFor?: 'SALON' | 'CLINIC' | 'BOTH' | null
}

export interface EquipmentAdDto extends SaveEquipmentAdRequest {
	id?: string
	status?: string
	mediaFileUrls?: string[] | null
	categoryTitle?: string | null
	subcategoryTitle?: string | null
	brandTitleResolved?: string | null
	cityName?: string | null
	createdDate?: string | null
}

/* ── Dictionary endpoints (equipment-dict — live) ─────────────────────────── */

export interface EquipmentDictItem {
	id?: string
	code?: string | null
	title?: string | null
	sortOrder?: number | null
	/** نوع دسته در دیکشنری: EQUIPMENT | MATERIAL (برای گروه‌بندی دسته‌ها). */
	kind?: string | null
}

export interface EquipmentDynamicFieldOption {
	id?: string
	value?: string | null
}

export interface EquipmentDynamicField {
	id?: string
	fieldKey?: string | null
	label?: string | null
	fieldType?: 'TEXT' | 'NUMBER' | 'SELECT' | 'MULTISELECT' | 'BOOLEAN'
	required?: boolean | null
	options?: EquipmentDynamicFieldOption[] | null
}

/**
 * Manually-written client for the equipment-ad endpoints (marketplace M1).
 * Same bridge pattern as CourseAdService — switch to the generated client
 * (imports only) once this backend ships and the API is regenerated.
 *
 * `equipment-dict/*` and `city/search` are live; `equipmentad/*` is not yet
 * deployed — callers must handle the resulting errors gracefully.
 */
@Injectable({ providedIn: 'root' })
export class EquipmentAdService {
	private http = inject(HttpClient)
	private base = environment.apiUrl

	saveMyDraft(request: SaveEquipmentAdRequest): Observable<EquipmentAdDto> {
		return this.http.put<EquipmentAdDto>(`${this.base}/equipmentad/save-my-draft`, request)
	}

	/** Best-guess rail mirroring course-ad — not live yet; wired for when it ships. */
	submit(id: string): Observable<EquipmentAdDto> {
		return this.http.post<EquipmentAdDto>(`${this.base}/equipmentad/${id}/submit`, {})
	}

	getPreview(id: string): Observable<EquipmentAdDto> {
		return this.http.get<EquipmentAdDto>(`${this.base}/equipmentad/preview/${id}`)
	}

	/* Dictionary lookups */

	getCategories(): Observable<EquipmentDictItem[]> {
		return this.http.get<EquipmentDictItem[]>(`${this.base}/equipment-dict/categories`)
	}

	getSubcategories(categoryId: string): Observable<EquipmentDictItem[]> {
		return this.http.get<EquipmentDictItem[]>(`${this.base}/equipment-dict/subcategories`, {
			params: { categoryId },
		})
	}

	getBrands(term?: string | null): Observable<EquipmentDictItem[]> {
		const params: Record<string, string> = {}
		if (term && term.trim().length > 0) params['term'] = term.trim()
		return this.http.get<EquipmentDictItem[]>(`${this.base}/equipment-dict/brands`, { params })
	}

	getModels(brandId: string, term?: string | null): Observable<EquipmentDictItem[]> {
		const params: Record<string, string> = { brandId }
		if (term && term.trim().length > 0) params['term'] = term.trim()
		return this.http.get<EquipmentDictItem[]>(`${this.base}/equipment-dict/models`, { params })
	}

	getDynamicFields(categoryId: string): Observable<EquipmentDynamicField[]> {
		return this.http.get<EquipmentDynamicField[]>(`${this.base}/equipment-dict/dynamic-fields`, {
			params: { categoryId },
		})
	}

	/* Owner lifecycle — «تجهیزات من» */

	getMyPage(statuses?: string[] | null, page = 0, size = 20): Observable<MyEquipmentPage> {
		let params: Record<string, string> = { page: String(page), size: String(size) }
		if (statuses && statuses.length > 0) params = { ...params, statuses: statuses.join(',') }
		return this.http.get<MyEquipmentPage>(`${this.base}/equipmentad/my-page`, { params })
	}

	duplicate(id: string): Observable<EquipmentAdDto> {
		return this.http.post<EquipmentAdDto>(`${this.base}/equipmentad/${id}/duplicate`, {})
	}

	/** TKT-00091: دلیل حذف (اختیاری) همراه با حذف نرم آگهی ارسال می‌شود. */
	deleteAd(id: string, reason?: string | null, reasonNote?: string | null): Observable<unknown> {
		let params: Record<string, string> = {}
		if (reason) params = { ...params, reason }
		if (reasonNote) params = { ...params, reasonNote }
		return this.http.delete(`${this.base}/equipmentad/${id}`, { params })
	}

	/** بستن/بازگشایی آگهی زنده (PUBLISHED ↔ PAUSED). */
	closeToggle(id: string): Observable<EquipmentAdDto> {
		return this.http.post<EquipmentAdDto>(`${this.base}/equipmentad/${id}/close`, {})
	}

	/* Payments — همان ریل requestCreateAd با نوع EQUIPMENT */

	requestListingPayment(adId: string, couponCode?: string | null): Observable<{ trackId?: string; payLink?: string }> {
		return this.http.post<{ trackId?: string; payLink?: string }>(`${this.base}/payment/create-ad/request`, {
			adId,
			adPriceType: 'EQUIPMENT',
			couponCode: couponCode || null,
		})
	}

	requestRenewalPayment(adId: string, couponCode?: string | null): Observable<{ trackId?: string; payLink?: string }> {
		return this.http.post<{ trackId?: string; payLink?: string }>(`${this.base}/payment/create-ad/request`, {
			adId,
			adPriceType: 'EQUIPMENT_RENEWAL',
			couponCode: couponCode || null,
		})
	}
}

/** صفحه my-page مالک — فقط فیلدهایی که UI لیست لازم دارد. */
export interface MyEquipmentPage {
	content?: MyEquipmentRow[]
	totalElements?: number
	totalPages?: number
	number?: number
	size?: number
}

export interface MyEquipmentRow {
	id?: string
	title?: string
	status?: string
	slug?: string
	categoryTitle?: string
	subcategoryTitle?: string
	brandTitle?: string
	modelTitle?: string
	condition?: string
	price?: number | null
	discountPrice?: number | null
	cityName?: string
	mediaUrls?: string[] | null
	agreedPrice?: number | null
	adminResponse?: string | null
	publishDate?: string | null
	expireDate?: string | null
	createdDate?: string | null
}
